import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useUserStats } from '../context/UserStatsContext';
import { Card, DailyGoalProgress, WelcomeModal, AnimatedBackground } from '../components/common';
import UserProfileDropdown from '../components/common/UserProfileDropdown';
import { DAILY_GOAL_MINUTES } from '../types';

const WELCOME_SHOWN_KEY = 'kids_welcome_shown_session';

const sections = [
  {
    title: 'Grammar',
    description: 'Listen and type what you hear!',
    icon: '\u{1F4DD}',
    color: 'indigo' as const,
    path: '/app/grammar',
  },
  {
    title: 'Reading',
    description: 'Read fun stories and answer questions!',
    icon: '\u{1F4DA}',
    color: 'emerald' as const,
    path: '/app/reading',
  },
  {
    title: 'Listening',
    description: 'Listen carefully and choose the right answer!',
    icon: '\u{1F3A7}',
    color: 'amber' as const,
    path: '/app/listening',
  },
  {
    title: 'Speaking',
    description: 'Practice saying words and sentences!',
    icon: '\u{1F399}',
    color: 'pink' as const,
    path: '/app/speaking',
  },
];

const HubPage: React.FC = () => {
  const { profile, setComplexity } = useUser();
  const { dailyProgress, dailyMinutes, goalReached, refreshStats } = useUserStats();
  // Show welcome message only after fresh login (not on refresh/navigation)
  const [showWelcome, setShowWelcome] = useState(() => {
    // Check if welcome was already shown in this session
    const wasShown = sessionStorage.getItem(WELCOME_SHOWN_KEY);
    return !wasShown;
  });

  // Refresh stats when HubPage mounts (coming back from practice)
  useEffect(() => {
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWelcomeClose = () => {
    sessionStorage.setItem(WELCOME_SHOWN_KEY, 'true');
    setShowWelcome(false);
  };

  const complexityLabels = {
    basic: { label: 'Easy', emoji: '\u{1F331}' },
    intermediate: { label: 'Medium', emoji: '\u{1F333}' },
    advanced: { label: 'Hard', emoji: '\u{1F3C6}' },
  };

  const currentLevel = profile?.complexity
    ? complexityLabels[profile.complexity]
    : complexityLabels.basic;

  const cycleComplexity = async () => {
    if (!profile) return;

    const levels: Array<'basic' | 'intermediate' | 'advanced'> = ['basic', 'intermediate', 'advanced'];
    const currentIndex = levels.indexOf(profile.complexity);
    const nextIndex = (currentIndex + 1) % levels.length;

    try {
      await setComplexity(levels[nextIndex]);
    } catch (error) {
      console.error('Failed to change level:', error);
    }
  };

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900 relative overflow-hidden">
      {/* Animated Background - Ocean theme */}
      <AnimatedBackground variant="ocean" />

      {/* Header */}
      <header className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-md dark:shadow-slate-900 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{'\u{1F433}'}</span>
            <h1 className="text-2xl font-bold text-kids-primary dark:text-indigo-400">Litrlly Kids</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Level indicator */}
            <button
              onClick={cycleComplexity}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 px-4 py-2 rounded-xl transition-colors"
              title="Click to change level"
            >
              <span className="text-xl">{currentLevel.emoji}</span>
              <span className="font-bold text-gray-700 dark:text-gray-200">{currentLevel.label}</span>
            </button>

            {/* User profile dropdown */}
            <UserProfileDropdown />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-8 relative z-[1]">
        {/* Daily Goal Card */}
        <div className={`bg-white dark:bg-slate-800 rounded-3xl shadow-lg dark:shadow-slate-900 p-6 mb-8 ${goalReached ? 'animate-pulse-glow' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-1">
                {goalReached ? 'Goal Reached!' : 'Daily Goal'} {goalReached ? '\u{1F389}' : '\u{1F3AF}'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                {goalReached
                  ? 'Amazing! You completed your goal today!'
                  : `Practice ${DAILY_GOAL_MINUTES} minutes every day`}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      goalReached ? 'bg-emerald-500' : 'bg-indigo-500'
                    }`}
                    style={{ width: `${dailyProgress}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-gray-600 dark:text-gray-300">
                  {dailyMinutes}/{DAILY_GOAL_MINUTES} min
                </span>
              </div>
            </div>
            <div className="ml-6">
              <DailyGoalProgress
                progress={dailyProgress}
                minutes={dailyMinutes}
                goalReached={goalReached}
                size="large"
              />
            </div>
          </div>
        </div>

        {/* Welcome message */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
            What do you want to learn? {'\u{1F914}'}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">Pick a section and start practicing!</p>
        </div>

        {/* Section cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {sections.map((section) => (
            <Card
              key={section.path}
              title={section.title}
              description={section.description}
              icon={section.icon}
              color={section.color}
              to={section.path}
            />
          ))}
        </div>

        {/* Footer message */}
        <div className="text-center mt-12">
          <p className="text-gray-500 dark:text-gray-400">
            Tip: Click the level button ({currentLevel.emoji} {currentLevel.label}) to change difficulty!
          </p>
        </div>
      </main>

      {/* Welcome Modal for first-time users */}
      <WelcomeModal
        isOpen={showWelcome}
        onClose={handleWelcomeClose}
        userName={profile?.email?.split('@')[0] || 'Friend'}
      />
    </div>
  );
};

export default HubPage;
